import {
  BASE_PATH,
  NAME_MAX_LENGTH,
  PLAYER_COLORS,
  rank,
  sanitizeName,
  SIZES,
  SYMBOLS,
  TURN_MS,
  type BoardView,
  type SeriesInfo,
  type SeriesStanding,
  type RankingEntry,
  type Size,
  type ErrorCode,
  type PlayerInfo,
  type RoomStatus,
  type ServerMsg,
} from "../src/protocol";
import { track } from "./analytics";
import { openDialog } from "./dialog";
import { h } from "./dom";
import { brandMark, siteFooter } from "./home";
import { copyIcon, exitIcon, shareIcon } from "./icons";
import { uiLangSelect } from "./langPicker";
import { Connection, type ConnectionStatus } from "./net";
import { qrSvg } from "./qr";
import { shareButton, type ResultCard } from "./shareCard";
import { getName, getPlayerId, setName } from "./storage";
import { STRINGS } from "./strings";
import { formatClock } from "./timing";

const CANONICAL_ORIGIN = "https://workbrawl.com";
const TOAST_MS = 2500;
/** The clock is green until this much time is left, then shifts gradually towards red… */
const CLOCK_WARN_MS = 30_000;
/** …which it reaches here: the last seconds are solid red and blink. */
const CLOCK_FINAL_MS = 10_000;
const CLOCK_GREEN_HUE = 135;
/** How often the clock refreshes. The server sends no ticks. */
const REFRESH_MS = 100;
/** The turn bar turns red when this little is left. */
const TURN_LOW_MS = 3000;

/** Room screen. Asks for a name first when the visitor arrived by link without one. */
export function renderRoom(root: HTMLElement, code: string): void {
  const name = sanitizeName(getName());
  if (name === null) renderNamePrompt(root, code);
  else new RoomScreen(root, code, name);
}

function renderNamePrompt(root: HTMLElement, code: string): void {
  const input = h("input", {
    id: "name",
    type: "text",
    autocomplete: "nickname",
    maxlength: NAME_MAX_LENGTH,
    placeholder: STRINGS.namePlaceholder,
    required: true,
  });
  const error = h("p", { class: "form-error", role: "alert" });
  const form = h(
    "form",
    { class: "card name-prompt", novalidate: true },
    h("h2", {}, STRINGS.namePromptTitle),
    h("p", { class: "muted" }, STRINGS.namePromptBody),
    h("label", { for: "name" }, STRINGS.nameLabel),
    input,
    h("button", { type: "submit", class: "btn btn-primary" }, STRINGS.enterRoom),
    error,
  );
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const name = sanitizeName(input.value);
    if (name === null) {
      error.textContent = STRINGS.nameInvalid;
      input.focus();
      return;
    }
    setName(name);
    new RoomScreen(root, code, name);
  });
  root.replaceChildren(
    h("main", { class: "center-screen" }, h("h1", { class: "brand brand-large" }, brandMark()), form),
    siteFooter(),
  );
  input.focus();
}

class RoomScreen {
  private readonly connection: Connection;
  private readonly toastEl = h("div", { class: "toast", role: "status", "aria-live": "polite", hidden: true });
  private readonly clockEl = h("strong", { class: "clock", "aria-label": STRINGS.timeLeft }, "0:00");
  private readonly headingEl = h("span", { class: "muted" }, STRINGS.timeLeft);
  private readonly pairsEl = h("span", { class: "stage-pairs" });
  private readonly statusEl = h("strong", { class: "stage-status", "aria-live": "polite" });
  /** Whose turn it is, in their color, and how long they have. */
  private readonly turnEl = h("p", { class: "turn-banner", "aria-live": "polite" });
  private readonly turnFill = h("div", { class: "turn-bar-fill" });
  private readonly turnBar = h(
    "div",
    { class: "turn-bar", role: "progressbar", "aria-label": STRINGS.turnTime },
    this.turnFill,
  );
  private readonly boardEl = h("div", { class: "cardboard", role: "grid", "aria-label": STRINGS.boardLabel });
  private readonly overlayEl = h("div", { class: "arena-overlay" });
  private readonly playersEl = h("ol", { class: "player-list" });

  private you = "";
  private status: RoomStatus = "lobby";
  private size: Size = "medium";
  private players = new Map<string, PlayerInfo>();
  private ranking: RankingEntry[] = [];
  private series: SeriesInfo = { length: 1, index: 1, standings: [] };
  /** Local time at which the current turn runs out. */
  private turnDeadline = 0;
  /** Round length of the room, for the shared result. */
  private roundSeconds = 0;
  private board: BoardView | null = null;
  /** Local time at which the countdown or the round ends. */
  private deadline = 0;
  private refreshTimer: ReturnType<typeof setInterval> | undefined;
  private toastTimer: ReturnType<typeof setTimeout> | undefined;
  /** One button per card, rebuilt only when the board size changes. */
  private cards: HTMLButtonElement[] = [];
  private cardsFor = "";
  private fatal = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly code: string,
    name: string,
  ) {
    const stage = h(
      "div",
      { class: "card stage" },
      h("div", { class: "stage-bar" }, this.pairsEl, this.statusEl),
      this.turnEl,
      this.turnBar,
      this.boardEl,
      this.overlayEl,
    );

    root.replaceChildren(
      this.header(),
      this.toastEl,
      h(
        "main",
        { class: "arena-layout" },
        h("section", { class: "arena-area" }, h("div", { class: "arena-hud" }, this.headingEl, this.clockEl), stage),
        h(
          "aside",
          { class: "panel" },
          h("section", { class: "card" }, h("h3", { class: "panel-title" }, STRINGS.scoreboard), this.playersEl),
          this.inviteCard(),
        ),
      ),
      siteFooter(),
    );

    this.connection = new Connection(Connection.roomUrl(code, getPlayerId(), name), {
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (status) => this.onStatus(status),
    });
    this.connection.connect();
  }

  private header(): HTMLElement {
    const exit = h(
      "button",
      { type: "button", class: "btn btn-small btn-exit", "aria-haspopup": "dialog", "aria-label": STRINGS.exit },
      exitIcon(),
      // Hidden on narrow screens, where the button is icon-only.
      h("span", { class: "btn-label" }, STRINGS.exit),
    );
    exit.addEventListener("click", () => this.confirmExit());
    return h(
      "header",
      { class: "topbar" },
      h("a", { href: `${BASE_PATH}/`, class: "brand" }, brandMark()),
      h(
        "div",
        { class: "room-info" },
        h("span", { class: "muted room-label" }, STRINGS.room),
        h("strong", { class: "room-code" }, this.code),
        uiLangSelect(true),
        exit,
      ),
    );
  }

  private confirmExit(): void {
    const cancel = h("button", { type: "button", class: "btn" }, STRINGS.cancel);
    const leave = h("button", { type: "button", class: "btn btn-danger" }, exitIcon(), STRINGS.exit);
    const dialog = openDialog(
      "confirm",
      h("h2", { class: "dialog-title" }, STRINGS.exitTitle),
      h("p", { class: "muted" }, STRINGS.exitBody),
      h("div", { class: "dialog-actions" }, cancel, leave),
    );
    cancel.addEventListener("click", () => dialog.close());
    leave.addEventListener("click", () => {
      // Suppress the "connection lost" message while we leave on purpose.
      this.fatal = true;
      this.connection.stop();
      location.assign(`${BASE_PATH}/`);
    });
    cancel.focus();
  }

  /** Copies the invite link; reports through a floating message unless `onDone` handles it. */
  private async copyInvite(onDone?: (ok: boolean) => void): Promise<void> {
    const url = inviteUrl(this.code);
    const ok = await copyText(url);
    if (onDone) onDone(ok);
    else this.toast(ok ? STRINGS.linkCopiedToast : `${STRINGS.copyFailed} ${url}`);
  }

  /** Invite card under the scoreboard: title with share/copy actions on the right. */
  private inviteCard(): HTMLElement {
    const share = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.share, "aria-label": STRINGS.share, "aria-haspopup": "dialog" },
      shareIcon(),
    );
    share.addEventListener("click", () => this.openInvite());
    const copy = h(
      "button",
      { type: "button", class: "icon-btn", title: STRINGS.copyLink, "aria-label": STRINGS.copyLink },
      copyIcon(),
    );
    copy.addEventListener("click", () => this.copyInvite());

    return h(
      "section",
      { class: "card invite-card" },
      h(
        "div",
        { class: "invite-head" },
        h("h3", { class: "panel-title" }, STRINGS.inviteTitle),
        h("div", { class: "icon-actions" }, share, copy),
      ),
    );
  }

  /** Modal with a large QR, the link, copy and (where supported) the native share sheet. */
  private openInvite(): void {
    const url = inviteUrl(this.code);
    const copy = h("button", { type: "button", class: "btn" }, copyIcon(), STRINGS.copyLink);
    copy.addEventListener("click", () =>
      this.copyInvite((ok) => {
        copy.replaceChildren(copyIcon(), ok ? STRINGS.linkCopied : STRINGS.copyFailedShort);
        setTimeout(() => copy.replaceChildren(copyIcon(), STRINGS.copyLink), 1500);
      }),
    );
    const close = h("button", { type: "button", class: "btn" }, STRINGS.close);

    const actions: HTMLElement[] = [];
    if (typeof navigator.share === "function") {
      const native = h("button", { type: "button", class: "btn btn-primary" }, shareIcon(), STRINGS.share);
      native.addEventListener("click", () => {
        navigator.share({ title: STRINGS.appName, text: STRINGS.shareText, url }).catch(() => {
          // The share sheet was dismissed: nothing to do.
        });
      });
      actions.push(native);
    }
    actions.push(copy, close);

    const dialog = openDialog(
      "invite",
      h("h2", { class: "dialog-title" }, STRINGS.inviteTitle),
      h("div", { class: "qr-box qr-box-large" }, qrSvg(url, STRINGS.qrLabel(this.code))),
      h("p", { class: "invite-url" }, url),
      h("div", { class: "dialog-actions" }, ...actions),
    );
    close.addEventListener("click", () => dialog.close());
  }

  private mySlot(): number | undefined {
    return this.players.get(this.you)?.slot;
  }

  /** Only the person whose turn it is can flip; everyone else waits. */
  private myTurn(): boolean {
    const board = this.board;
    return this.status === "running" && board?.phase === "open" && board.turn === this.mySlot();
  }

  private flip(cell: number): void {
    if (this.myTurn()) this.connection.send({ type: "flip", cell });
  }

  /** Buttons for a cols × rows board. */
  private buildCards(cols: number, rows: number): void {
    const key = `${cols}x${rows}`;
    if (this.cardsFor === key) return;
    this.cardsFor = key;
    this.boardEl.style.setProperty("--cols", String(cols));
    // The extra large board needs more of the screen than the usual layout gives it.
    document.body.classList.toggle("wide-board", cols >= 8);
    this.cards = Array.from({ length: cols * rows }, (_, cell) => {
      const button = h("button", { type: "button", class: "memo-card", role: "gridcell" });
      button.addEventListener("click", () => this.flip(cell));
      return button;
    });
    this.boardEl.replaceChildren(...this.cards);
  }

  private onStatus(status: ConnectionStatus): void {
    if (this.fatal) return;
    if (status === "connecting") this.toast(STRINGS.connecting, true);
    else if (status === "reconnecting") this.toast(STRINGS.reconnecting, true);
    else if (status === "open") this.hideToast();
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.type) {
      case "state": {
        const previous = this.status;
        this.you = msg.you;
        this.status = msg.status;
        this.roundSeconds = msg.duration;
        // One per person per round: this is what "how much is each title used" is measured by.
        if (msg.status === "running" && previous !== "running") {
          track("round_started", { people: msg.players.length, duration: msg.duration });
        } else if (msg.status === "finished" && previous === "running") {
          track("round_finished", { people: msg.players.length, rounds: msg.series.length, series_round: msg.series.index });
        }
        this.series = msg.series;
        this.size = msg.size;
        this.players = new Map(msg.players.map((p) => [p.id, p]));
        this.ranking = msg.ranking;
        this.deadline = Date.now() + msg.remainingMs;
        if (msg.status !== "running") this.board = null;
        this.renderStatus();
        this.renderBoard();
        this.renderTeam();
        break;
      }
      case "board": {
        for (const [slot, score] of msg.p) {
          for (const player of this.players.values()) if (player.slot === slot) player.score = score;
        }
        // A pair just found by this person: a short buzz.
        const before = this.board?.taken.length ?? 0;
        const me = this.players.get(this.you)?.slot;
        if (msg.taken.length > before && msg.taken.some(([cell, , slot]) => slot === me && msg.last?.includes(cell))) {
          navigator.vibrate?.(30);
        }
        // My turn just started: a short buzz so it isn't missed.
        if (msg.turn === me && msg.phase === "open" && this.board?.turn !== me) navigator.vibrate?.(20);
        this.board = msg;
        this.deadline = Date.now() + msg.t;
        this.turnDeadline = Date.now() + msg.turnMs;
        this.renderBoard();
        this.renderTeam();
        this.refresh();
        break;
      }
      case "player_joined":
      case "player_updated":
        this.players.set(msg.player.id, msg.player);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "player_left":
        this.players.delete(msg.id);
        this.renderTeam();
        if (this.status === "lobby") this.renderStatus();
        break;
      case "error":
        this.onError(msg.code);
        break;
      case "pong":
        break;
    }
  }

  private onError(code: ErrorCode): void {
    if (code === "BAD_MESSAGE") return;
    this.fatal = true;
    this.connection.stop();
    clearInterval(this.refreshTimer);
    this.root.replaceChildren(
      h(
        "main",
        { class: "center-screen" },
        h("h1", { class: "brand brand-large" }, brandMark()),
        h(
          "section",
          { class: "card center-card" },
          h("p", {}, code === "ROOM_FULL" ? STRINGS.errorRoomFull : STRINGS.errorRoomNotFound),
          h("a", { href: `${BASE_PATH}/`, class: "btn btn-primary" }, STRINGS.backHome),
        ),
      ),
      siteFooter(),
    );
  }

  /** Overlay and timers for the current phase of the room. */
  private renderStatus(): void {
    clearInterval(this.refreshTimer);
    this.refreshTimer = undefined;
    this.overlayEl.hidden = this.status === "running";

    if (this.status === "lobby") {
      this.setClock(0);
      this.overlayEl.replaceChildren(
        h("h2", {}, STRINGS.lobbyTitle),
        h("p", {}, STRINGS.lobbyBody(this.onlineCount())),
        this.startButton(STRINGS.startRound),
      );
    } else if (this.status === "countdown") {
      const number = h("strong", { class: "countdown" });
      const update = () => {
        const next = String(Math.max(1, Math.ceil((this.deadline - Date.now()) / 1000)));
        if (number.textContent === next) return;
        number.textContent = next;
        // Restart the zoom-out for each new number (reading offsetWidth forces a reflow).
        number.classList.remove("countdown-pop");
        void number.offsetWidth;
        number.classList.add("countdown-pop");
      };
      update();
      this.refreshTimer = setInterval(update, REFRESH_MS);
      this.overlayEl.replaceChildren(h("p", {}, STRINGS.getReady), number);
    } else if (this.status === "finished") {
      this.setClock(0);
      this.overlayEl.replaceChildren(...this.resultsContent());
    } else {
      this.refresh();
      this.refreshTimer = setInterval(() => this.refresh(), REFRESH_MS);
    }
  }

  /**
   * A single round: its podium. Mid-series: the round's podium plus the standings so far.
   * After the last round of a series: the final standings, with every round's points.
   */
  private resultsContent(): HTMLElement[] {
    const { series } = this;
    const inSeries = series.length > 1;
    const final = inSeries && series.index >= series.length;
    const content: HTMLElement[] = [];
    if (final) {
      const [first, second] = series.standings;
      const headline = !first
        ? ""
        : second && second.total === first.total
          ? STRINGS.seriesTie
          : first.id === this.you
            ? STRINGS.seriesYouWon
            : STRINGS.seriesWinner(first.name);
      content.push(
        h("h2", {}, STRINGS.finalResults),
        h("p", { class: "results-headline" }, headline),
        h("h3", { class: "panel-title" }, STRINGS.standingsTitle),
        standingsTable(series.standings, this.you, true),
      );
    } else {
      const [first, second] = this.ranking;
      const headline = !first ? "" : second && second.score === first.score ? STRINGS.tie : first.id === this.you ? STRINGS.youWin : STRINGS.winner(first.name);
      content.push(
        h("h2", {}, inSeries ? STRINGS.roundDone(series.index, series.length) : STRINGS.resultsTitle),
        h("p", { class: "results-headline" }, headline),
        h(
          "ol",
          { class: "ranking" },
          // The podium only: the full order is in the scoreboard next to the board.
          ...this.ranking.slice(0, 3).map((entry, i) => {
            const row = h(
              "li",
              { class: i === 0 ? "ranking-row ranking-first" : "ranking-row" },
              h("span", { class: "ranking-pos" }, `${i + 1}`),
              h("span", { class: "avatar", "aria-hidden": "true" }, initial(entry.name)),
              h("span", { class: "player-name" }, entry.name),
              h("span", { class: "player-score" }, STRINGS.points(entry.score)),
            );
            row.style.setProperty("--player", PLAYER_COLORS[entry.slot % PLAYER_COLORS.length]!);
            return row;
          }),
        ),
      );
      if (inSeries) {
        content.push(h("h3", { class: "panel-title" }, STRINGS.standingsSoFar), standingsTable(series.standings, this.you, false));
      }
    }
    const label = !inSeries ? STRINGS.newRound : final ? STRINGS.newSeries : STRINGS.nextRound(series.index + 1, series.length);
    content.push(this.startButton(label), shareButton(() => this.resultCard()));
    return content;
  }

  /**
   * What "Share result" draws: the podium, worded for people who weren't in the room. After
   * the last round of a series, the series totals.
   */
  private resultCard(): ResultCard {
    const { series } = this;
    const final = series.length > 1 && series.index >= series.length;
    const entries = final
      ? series.standings.map((s) => ({ name: s.name, slot: s.slot, score: s.total }))
      : this.ranking;
    const [first, second] = entries;
    const tie = second !== undefined && first !== undefined && second.score === first.score;
    return {
      title: "MemoBrawl",
      slug: "memobrawl",
      accent: "#7048e8",
      headline: !first
        ? STRINGS.resultsTitle
        : tie
          ? final
            ? STRINGS.seriesTie
            : STRINGS.tie
          : final
            ? STRINGS.seriesWinner(first.name)
            : STRINGS.winner(first.name),
      podium: entries.slice(0, 3).map((entry) => ({
        name: entry.name,
        score: STRINGS.points(entry.score),
        color: PLAYER_COLORS[entry.slot % PLAYER_COLORS.length]!,
      })),
      people: entries.length || this.players.size,
      minutes: Math.round(this.roundSeconds / 60),
      rounds: final ? series.length : 1,
    };
  }

  private startButton(label: string): HTMLElement {
    const button = h("button", { type: "button", class: "btn btn-primary" }, label);
    button.addEventListener("click", () => this.connection.send({ type: "start" }));
    return button;
  }

  /** Pairs left, whose turn it is, and every card of the board (numbered while face down). */
  private renderBoard(): void {
    const board = this.board;
    if (!board) {
      this.headingEl.textContent = this.roundLabel() ?? STRINGS.timeLeft;
      this.pairsEl.textContent = "";
      this.statusEl.textContent = "";
      this.turnEl.hidden = true;
      this.turnBar.hidden = true;
      for (const card of this.cards) {
        card.className = "memo-card is-down";
        card.textContent = "";
        card.disabled = true;
      }
      return;
    }

    this.buildCards(board.cols, board.rows);
    const size = sizeOf(board.cols, board.rows);
    const round = this.roundLabel();
    this.headingEl.textContent = round ? `${round} · ${STRINGS.sizeNames[size]}` : STRINGS.sizeNames[size];
    const left = (board.cols * board.rows) / 2 - board.taken.length / 2;
    this.pairsEl.textContent = STRINGS.pairsLeft(left);
    this.statusEl.textContent = board.phase === "cleared" ? STRINGS.clearedBanner : board.phase === "miss" ? STRINGS.missBanner : "";
    this.statusEl.className = `stage-status${board.phase === "cleared" ? " is-cleared" : ""}`;

    const nameOf = (slot: number) => [...this.players.values()].find((p) => p.slot === slot)?.name ?? "";
    const mine = this.myTurn();
    this.renderTurn();

    const up = new Map(board.up.map(([cell, symbol, slot]) => [cell, [symbol, slot] as const]));
    const taken = new Map(board.taken.map(([cell, symbol, slot]) => [cell, [symbol, slot] as const]));
    const fresh = new Set(board.last ?? []);
    this.boardEl.classList.toggle("is-waiting", !mine && board.phase !== "cleared");
    this.cards.forEach((button, cell) => {
      const shown = up.get(cell) ?? taken.get(cell);
      let className = "memo-card is-down";
      let label = STRINGS.cardDown(cell + 1);
      let text = String(cell + 1);
      if (shown) {
        const [symbol, slot] = shown;
        const emoji = SYMBOLS[symbol] ?? "?";
        className = taken.has(cell) ? "memo-card is-up is-taken" : "memo-card is-up";
        if (taken.has(cell) && fresh.has(cell)) className += " is-new";
        label = taken.has(cell) ? STRINGS.cardTaken(cell + 1, emoji, nameOf(slot)) : STRINGS.cardUp(cell + 1, emoji);
        text = emoji;
        // The color of whoever flipped the card, or found its pair.
        button.style.setProperty("--by", PLAYER_COLORS[slot % PLAYER_COLORS.length]!);
      } else {
        button.style.removeProperty("--by");
      }
      if (button.textContent !== text) button.textContent = text;
      if (button.className !== className) button.className = className;
      button.setAttribute("aria-label", label);
      button.disabled = !mine || shown !== undefined;
    });
  }

  /** "Round 2 of 3" in a series, nothing for a single round. */
  private roundLabel(): string | null {
    return this.series.length > 1 ? STRINGS.roundOf(this.series.index, this.series.length) : null;
  }

  /** The banner with whose turn it is, in their color, and the bar with their time. */
  private renderTurn(): void {
    const board = this.board;
    const turn = board && board.phase !== "cleared" ? board.turn : -1;
    const person = [...this.players.values()].find((p) => p.slot === turn);
    this.turnEl.hidden = !person;
    this.turnBar.hidden = !person;
    if (!person) return;
    const mine = person.id === this.you;
    this.turnEl.replaceChildren(
      h("span", { class: "avatar", "aria-hidden": "true" }, initial(person.name)),
      h("strong", {}, mine ? STRINGS.yourTurn : STRINGS.turnOf(person.name)),
      mine ? "" : h("span", { class: "muted" }, ` · ${STRINGS.waitTurn}`),
    );
    this.turnEl.classList.toggle("is-mine", mine);
    this.turnEl.style.setProperty("--player", PLAYER_COLORS[person.slot % PLAYER_COLORS.length]!);
    this.turnFill.classList.toggle("is-mine", mine);
    this.renderTurnTime();
  }

  private renderTurnTime(): void {
    if (this.turnBar.hidden) return;
    const left = Math.max(0, this.turnDeadline - Date.now());
    this.turnFill.style.width = `${Math.min(100, (left / TURN_MS) * 100)}%`;
    this.turnFill.classList.toggle("is-low", left <= TURN_LOW_MS);
  }

  /** Everyone's points, best first. */
  private renderTeam(): void {
    this.playersEl.replaceChildren(
      ...rank([...this.players.values()]).map((p) => {
        const turn = this.board && this.board.phase !== "cleared" && this.board.turn === p.slot;
        const row = h(
          "li",
          { class: `player${p.online ? "" : " player-offline"}${turn ? " player-turn" : ""}` },
          h("span", { class: "avatar", "aria-hidden": "true" }, initial(p.name)),
          h(
            "span",
            { class: "player-name" },
            p.name,
            p.id === this.you ? h("span", { class: "tag" }, STRINGS.you) : null,
            p.online ? null : h("span", { class: "tag" }, STRINGS.offline),
            turn ? h("span", { class: "tag tag-turn" }, STRINGS.turnTag) : null,
          ),
          h("span", { class: "player-score", title: STRINGS.points(p.score) }, String(p.score)),
        );
        row.style.setProperty("--player", PLAYER_COLORS[p.slot % PLAYER_COLORS.length]!);
        return row;
      }),
    );
  }

  /** Round clock and turn bar, from the local deadlines. */
  private refresh(): void {
    this.setClock(this.deadline - Date.now());
    this.renderTurnTime();
  }

  private onlineCount(): number {
    return [...this.players.values()].filter((p) => p.online).length;
  }

  private setClock(ms: number): void {
    this.clockEl.textContent = formatClock(ms);
    const running = this.status === "running";
    this.clockEl.classList.toggle("clock-running", running);
    this.clockEl.classList.toggle("clock-final", running && ms <= CLOCK_FINAL_MS);
    // Hue 135 (green) → 0 (red), passing through yellow and orange on the way.
    const left = Math.min(1, Math.max(0, (ms - CLOCK_FINAL_MS) / (CLOCK_WARN_MS - CLOCK_FINAL_MS)));
    this.clockEl.style.setProperty("--clock-hue", String(Math.round(CLOCK_GREEN_HUE * left)));
  }

  private toast(text: string, sticky = false): void {
    clearTimeout(this.toastTimer);
    this.toastEl.textContent = text;
    this.toastEl.hidden = false;
    if (!sticky) this.toastTimer = setTimeout(() => this.hideToast(), TOAST_MS);
  }

  private hideToast(): void {
    clearTimeout(this.toastTimer);
    this.toastEl.hidden = true;
  }
}

/** Clipboard API where available, with the legacy selection path as a fallback. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  const area = h("textarea", { readonly: true, class: "visually-hidden" });
  area.value = text;
  document.body.append(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  return ok;
}

function initial(name: string): string {
  return [...name][0]?.toUpperCase() ?? "?";
}

/** The size name of a cols × rows board. */
function sizeOf(cols: number, rows: number): Size {
  const found = (Object.keys(SIZES) as Size[]).find((id) => SIZES[id].cols === cols && SIZES[id].rows === rows);
  return found ?? "medium";
}

/**
 * Series standings: place, name, total and, with `perRound`, a column for each round. The
 * table scrolls sideways on narrow screens rather than squeezing the names.
 */
function standingsTable(standings: SeriesStanding[], you: string, perRound: boolean): HTMLElement {
  const places = standings.map((s) => 1 + standings.filter((o) => o.total > s.total).length);
  const rounds = perRound ? (standings[0]?.scores.length ?? 0) : 0;
  return h(
    "div",
    { class: "standings-wrap" },
    h(
      "table",
      { class: "standings" },
      h(
        "thead",
        {},
        h(
          "tr",
          {},
          h("th", { scope: "col", class: "num" }, "#"),
          h("th", { scope: "col" }, ""),
          ...Array.from({ length: rounds }, (_, i) => h("th", { scope: "col", class: "num" }, STRINGS.roundColumn(i + 1))),
          h("th", { scope: "col", class: "num" }, STRINGS.totalColumn),
        ),
      ),
      h(
        "tbody",
        {},
        ...standings.map((s, i) => {
          const row = h(
            "tr",
            { class: places[i] === 1 ? "standings-first" : "" },
            h("td", { class: "num" }, `${places[i]}º`),
            h(
              "th",
              { scope: "row" },
              h("span", { class: "avatar", "aria-hidden": "true" }, initial(s.name)),
              h("span", { class: "player-name" }, s.name),
              s.id === you ? h("span", { class: "tag" }, STRINGS.you) : null,
            ),
            ...(perRound ? s.scores.map((score) => h("td", { class: "num" }, String(score))) : []),
            h("td", { class: "num standings-total" }, String(s.total)),
          );
          row.style.setProperty("--player", PLAYER_COLORS[s.slot % PLAYER_COLORS.length]!);
          return row;
        }),
      ),
    ),
  );
}

/**
 * Invite links use the canonical domain, except when running locally or on
 * *.workers.dev, where the canonical link would point to a different deployment.
 */
function inviteUrl(code: string): string {
  const host = location.hostname;
  const isLocal =
    host === "localhost" ||
    /^(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(host) ||
    host.endsWith(".workers.dev");
  return `${isLocal ? location.origin : CANONICAL_ORIGIN}${BASE_PATH}/r/${code}`;
}
